import { Alert, App as AntApp, Breadcrumb, Button, Card, Descriptions, Grid, Skeleton, Space, Table, Tag, Typography } from "antd";
import { DeleteOutlined, EditOutlined, LockOutlined } from "@ant-design/icons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router";
import { api } from "../app/api.ts";
import { ApiError } from "../app/apiError.ts";
import { useAuth } from "../app/auth.tsx";
import { formatDate } from "../app/classes.ts";
import { examKeys, useExam, type ExamClassDetails } from "../app/exams.ts";
import { ExamDrawer } from "../components/ExamDrawer.tsx";
import { ExamMarksModal } from "../components/ExamMarksModal.tsx";

/**
 * One school-wide exam (bd-3qg.10): its subject, date and maximum, and per class the grader and the results so far (marks entered
 * and their average on the exam's scale). The grader, or staff holding grading.manage, opens a class to enter marks; the deputy
 * director (academic.manage) changes or deletes the exam.
 */
export function ExamDetailPage() {
  const { t } = useTranslation();
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { message, modal } = AntApp.useApp();
  const queryClient = useQueryClient();
  const screens = Grid.useBreakpoint();
  const { hasPermission } = useAuth();
  const canPlan = hasPermission("academic.manage");
  const exam = useExam(id);
  const [editing, setEditing] = useState(false);
  const [open, setOpen] = useState<ExamClassDetails>();

  const remove = useMutation({
    mutationFn: async () => {
      const { error, response } = await api.DELETE("/grading/exams/{examId}", { params: { path: { examId: id } } });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: examKeys.all });
      void message.success(t("exams.deleted"));
      void navigate("/grading/exams");
    },
    onError: (err) => void message.error((err instanceof ApiError ? err.messages(t("app.error")) : [t("app.error")]).join(" ")),
  });

  const confirmDelete = () =>
    modal.confirm({
      title: t("exams.deleteConfirm", { name: exam.data?.name ?? "" }),
      okText: t("common.delete"),
      okButtonProps: { danger: true },
      cancelText: t("common.cancel"),
      onOk: () => remove.mutateAsync().catch(() => undefined),
    });

  const columns = [
    { title: t("exams.class"), dataIndex: "className", key: "class", width: 100 },
    { title: t("exams.grader"), dataIndex: "graderName", key: "grader" },
    { title: t("exams.markCount"), key: "marks", width: 110, render: (_: unknown, c: ExamClassDetails) => Number(c.markCount) },
    {
      title: t("exams.average"),
      key: "average",
      width: 120,
      render: (_: unknown, c: ExamClassDetails) => (c.averageScore === null ? "—" : String(c.averageScore)),
    },
    {
      title: t("common.actions"),
      key: "actions",
      width: 170,
      render: (_: unknown, c: ExamClassDetails) => (
        <Space size={4} wrap>
          {c.isClosed && <Tag icon={<LockOutlined />} color="orange">{t("exams.closedShort")}</Tag>}
          <Button size="small" onClick={() => setOpen(c)} aria-label={t("exams.openClassNamed", { name: c.className })}>
            {c.mayEdit ? t("exams.enterMarks") : t("exams.viewResults")}
          </Button>
        </Space>
      ),
    },
  ];

  const data = exam.data;
  return (
    <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
      <Breadcrumb items={[{ title: <Link to="/grading/exams">{t("exams.title")}</Link> }, { title: data?.name ?? "" }]} />
      {exam.isError && <Alert type="error" showIcon title={t("app.error")} action={<Button onClick={() => void exam.refetch()}>{t("app.retry")}</Button>} />}
      {exam.isPending && <Skeleton active />}
      {data && (
        <>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", justifyContent: "space-between" }}>
            <Typography.Title level={3} style={{ margin: 0 }}>
              {data.name}
            </Typography.Title>
            {canPlan && (
              <Space wrap>
                <Button icon={<EditOutlined />} onClick={() => setEditing(true)}>
                  {t("common.edit")}
                </Button>
                <Button danger icon={<DeleteOutlined />} loading={remove.isPending} onClick={confirmDelete}>
                  {t("common.delete")}
                </Button>
              </Space>
            )}
          </div>
          <Descriptions size="small" column={screens.md ? 3 : 1} bordered>
            <Descriptions.Item label={t("exams.date")}>{formatDate(data.date)}</Descriptions.Item>
            <Descriptions.Item label={t("exams.subject")}>{data.subjectName}</Descriptions.Item>
            <Descriptions.Item label={t("exams.maxScore")}>{Number(data.maxScore)}</Descriptions.Item>
          </Descriptions>
          <Card title={t("exams.resultsTitle")}>
            <Typography.Paragraph type="secondary">{t("exams.resultsHint")}</Typography.Paragraph>
            <Table<ExamClassDetails>
              rowKey="classId"
              size={screens.md ? "middle" : "small"}
              columns={columns}
              dataSource={data.classes}
              pagination={false}
              scroll={{ x: "max-content" }}
            />
          </Card>
          <ExamDrawer open={editing} exam={data} onClose={() => setEditing(false)} />
          <ExamMarksModal exam={data} examClass={open} onClose={() => setOpen(undefined)} />
        </>
      )}
    </Space>
  );
}
